<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Rows
      </h1>
      <p class="page-title__sub">
        Utility classes to control a grid item row size and position.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.row-span-1</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 1 / span 1;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-span-2</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 2 / span 2;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-span-3</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 3 / span 3;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-span-4</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 4 / span 4;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-span-5</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 5 / span 5;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-span-6</code>
              </td>
              <td>
                <code class="code__prop">grid-row: span 6 / span 6;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-1</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 1;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-2</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 2;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-3</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 3;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-4</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 4;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-5</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 5;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-start-6</code>
              </td>
              <td>
                <code class="code__prop">grid-row-start: 6;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-1</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 1;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-2</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 2;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-3</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 3;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-4</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 4;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-5</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 5;</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.row-end-6</code>
              </td>
              <td>
                <code class="code__prop">grid-row-end: 6;</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Row span
        </h2>
      </header>
      <p>
        To set the row span for grid items suffix the span size,
        for example<code>.row-span-{n}</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-cols-6..."&gt;
  &lt;div class="item row-span-3"&gt;1&lt;div&gt;
  ...
  &lt;div class="item"&gt;9&lt;div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-6 grid-rows-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <span class="preview__button preview__button--auto col-span-2 row-span-3
            bg-tiara inline-flex align-items-center justify-content-center"
            >
              1
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              2
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              3
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              4
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              5
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              6
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              7
            </span>
            <span class="preview__button col-span-2 bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              8
            </span>
            <span class="preview__button col-span-4 bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              9
            </span>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Row start/end
        </h2>
      </header>
      <p>
        To set where a row starts and end suffix the grid row value,
        for example <code>.row-start-{n}</code> or <code>.row-end-{n}</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="grid grid-rows-4..."&gt;
  &lt;div class="item row-start-1 row-end-3"&gt;1&lt;div&gt;
  ...
  &lt;div class="item row-start-4"&gt;12&lt;div&gt;
&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-6 grid-rows-4 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <span class="preview__button preview__button--auto col-span-2 row-start-1 row-end-4
            bg-tiara inline-flex align-items-center justify-content-center"
            >
              1
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              2
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              3
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              4
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              5
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              6
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              7
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              8
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              9
            </span>
            <span class="preview__button bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              10
            </span>
            <span class="preview__button col-span-3 bg-tiara inline-flex
            align-items-center justify-content-center"
            >
              11
            </span>
            <span class="preview__button col-span-6 row-start-4
            bg-tiara inline-flex align-items-center justify-content-center"
            >
              12
            </span>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'Row',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>

<style scoped>

</style>
